import type { LoadingButtonProps } from '@zillow/constellation';
import {
  AdornedInput,
  Adornment,
  Alert,
  ButtonGroup,
  Card,
  CardSection,
  DetailedIconDollarHome,
  Flex,
  FormField,
  FormHelp,
  Grid,
  Heading,
  IconButton,
  IconCalendarOutline,
  IconPlusOutline,
  IconTrashcanOutline,
  Input,
  Label,
  LoadingButton,
  ModalDialog,
  Paragraph,
  Select,
  Spinner,
  Tag,
  Textarea,
  TextButton,
  Toast,
  Tooltip,
  TooltipPopper,
  Trigger,
  useToast,
} from '@zillow/constellation';
import { useEffectOnce } from 'react-use';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { get } from 'lodash-es';
import {
  AddDocumentsToIncomeSourceDocument,
  DeleteDocumentsFromIncomeSourceDocument,
  GetRentalApplicationIncomeDocsDocument,
  GetRentalApplicationsDocumentUploadTokenDocument,
  IncomeType,
} from '../../../../graphql/queries';
import type { Answers, Document, DocumentInput } from '../../../../graphql/queries';
import { validateDate, validateEmail } from '../../../../utils/validation';
import { MAX_DOCUMENTS_PER_INCOME, TEXT_AREA_MAX_LENGTH_510 } from '../../../../constants/form';
import { date50YearsAgo, isValidUSPhoneNumber } from '../../../../utils/formatters';
import type { ZDocsUpload } from '@zillow/zdocs-web';
import { ZDocsUploader } from '@zillow/zdocs-web';
import { useApolloClient, useMutation } from '@apollo/client';
import { DOCUMENTS_EXPIRED_TEXT, INCOME_DOC_ENCOURAGEMENT_TEXT } from '../../../../constants/incomeDocs';
import { IncomeDocPreviewImage, IncomeDocSpinnerSpacer } from '../../../../styles/styled-components';
import { MaskedCurrencyInput, MaskedPhoneInput } from '../../../Inputs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { emitAddMoreIncomeEvent, emitNextReviewIncomeSourcesEvent } from '../../../../analytics/clickstreamEvents';
import { DatePickerInputMemoized } from '../../../DatePicker';
import { useIncomeDocsRedaction } from '../../../../hooks/useIncomeDocsRedaction';
import { validateCurrency } from '../../../Inputs/MaskedCurrencyInput';
import { useApplicationContextOrUndefined } from '../../../../contexts/ApplicationContext';

const emptyCurrentIncome = {
  type: IncomeType.employment,
  startDate: '',
  employer: { organization: '', name: '', email: '', phone: '' },
  amount: '',
  title: '',
  documents: [],
  note: '',
};

export function CurrentIncomeStep({ onPersist }: { onPersist: () => Promise<void> }) {
  // Consider that useFieldArray substitutes ids for a random uuid, so item.id is
  // not stable and not the same as the backend id, see https://github.com/orgs/react-hook-form/discussions/8935
  // This is tracked in RA-6890
  const { fields, append, remove } = useFieldArray({ name: 'currentIncomes' });
  const applicationContext = useApplicationContextOrUndefined();

  useEffectOnce(() => {
    emitNextReviewIncomeSourcesEvent(applicationContext);
  });

  useEffect(() => {
    if (fields.length === 0) {
      onAddRow();
    }
  }, []);

  function onDeleteRow(index: number) {
    remove(index);
  }

  function onAddRow() {
    append(emptyCurrentIncome);
    onPersist();
  }

  return (
    <>
      <Heading level={4} as="h2" marginBottom="sm" fontFamily="serif">
        Current income
      </Heading>

      <Alert
        body="Avoid sharing Social Security number, date of birth, account numbers, or other personal information."
        marginBottom="sm"
      />

      <Flex display="flex" flexDirection="column" gap="sm">
        {fields.map((currentIncome, index) => (
          <CurrentIncomeEdit key={currentIncome.id} index={index} onDeleteRow={() => onDeleteRow(index)} />
        ))}
        <TextButton
          onClick={() => {
            emitAddMoreIncomeEvent(applicationContext);
            onAddRow();
          }}
          icon={<IconPlusOutline />}
          type="button"
        >
          Add income
        </TextButton>
      </Flex>
    </>
  );
}

export function CurrentIncomeStepTip() {
  return (
    <Card cardElevation={0} cardType="secondary">
      <DetailedIconDollarHome marginBottom="sm" />
      <Heading level={4} as="h2" marginBottom="sm" fontFamily="serif">
        Income source(s)
      </Heading>

      <Paragraph>
        Providing details about your income sources allows rental managers to verify your income and employment.
      </Paragraph>
    </Card>
  );
}

function CurrentIncomeEdit({ index, onDeleteRow }: { index: number; onDeleteRow: () => void }) {
  const {
    formState: { errors },
    register,
    setValue,
    watch,
  } = useFormContext();

  const incomeType = watch(`currentIncomes[${index}].type`);

  const errorAmount = get(errors, `currentIncomes[${index}].amount`);
  const errorTitle = get(errors, `currentIncomes[${index}].title`);
  return (
    <>
      <Flex display="flex" justifyContent="space-between" alignItems="flex-start">
        <Heading level={6} as="h3" fontColor="textMedium">
          Income source #{index + 1}
        </Heading>
        <TextButton onClick={onDeleteRow} fontType="bodySmallHeading" type="button">
          Delete
        </TextButton>
      </Flex>
      <Flex display="flex" flexDirection="column" gap="sm">
        <Controller
          name={`currentIncomes[${index}].type`}
          render={({ field: { onChange, value, onBlur } }) => (
            <FormField
              label={<Label>Type</Label>}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                if (e.target.value !== IncomeType.employment) {
                  [
                    `currentIncomes[${index}].employer.organization`,
                    `currentIncomes[${index}].startDate`,
                    `currentIncomes[${index}].employer.name`,
                    `currentIncomes[${index}].employer.email`,
                    `currentIncomes[${index}].employer.phone`,
                  ].forEach((fieldToClear) => setValue(fieldToClear, null));
                }
                onChange(e.target.value);
              }}
              control={
                <Select value={value || ''} onBlur={onBlur}>
                  <option value={IncomeType.employment}>Employment</option>
                  <option value={IncomeType.selfEmployed}>Contract or self-employment</option>
                  <option value={IncomeType.voucher}>Tenant-based voucher</option>
                  <option value={IncomeType.other}>Other (specify)</option>
                </Select>
              }
            />
          )}
        />
        <Controller
          name={`currentIncomes[${index}].amount`}
          rules={{ required: 'Income amount is required', validate: validateCurrency }}
          render={({ field }) => (
            <FormField
              label={
                <Label>Monthly {incomeType === IncomeType.voucher ? 'approved amount' : 'income before taxes'}</Label>
              }
              control={<MaskedCurrencyInput {...field} />}
              description={
                errorAmount ? (
                  <FormHelp>{errorAmount.message}</FormHelp>
                ) : (
                  incomeType === IncomeType.voucher && (
                    <FormHelp>Enter the voucher payment standard for your approved unit size in your area</FormHelp>
                  )
                )
              }
              error={!!errorAmount}
            />
          )}
        />
        <FormField
          label={<Label>{getIncomeTitle(incomeType)}</Label>}
          control={
            <Input
              maxLength={32}
              {...register(`currentIncomes[${index}].title`, { required: 'This field is required' })}
            />
          }
          description={errorTitle && <FormHelp>{errorTitle.message}</FormHelp>}
          error={!!errorTitle}
        />
        <IncomeDocs index={index} incomeType={incomeType} />
        {incomeType === IncomeType.employment && <EmployerContact index={index} />}
        <FormField
          label={<Label>Additional information</Label>}
          control={
            <Textarea
              maxLength={TEXT_AREA_MAX_LENGTH_510}
              {...register(`currentIncomes[${index}].note`)}
              resize="vertical"
            />
          }
          optional
        />
      </Flex>
    </>
  );
}

export function getIncomeTitle(incomeType: IncomeType) {
  return incomeType === IncomeType.employment
    ? 'Job title'
    : incomeType === IncomeType.voucher
      ? 'Issuing agency'
      : 'Income source description';
}

function IncomeDocs({ index, incomeType }: { index: number; incomeType: IncomeType }) {
  const { watch } = useFormContext();
  const areDocumentsExpired = watch(`currentIncomes[${index}].areDocumentsExpired`);

  return (
    <>
      <IncomeDocsHeader incomeType={incomeType} />
      {areDocumentsExpired && <Alert appearance="warning" body={DOCUMENTS_EXPIRED_TEXT} />}
      <IncomeDocsPrivacyProtection />
      <UploadedIncomeDocs index={index} />
      <IncomeDocsUploader index={index} />
    </>
  );
}

function IncomeDocsHeader({ incomeType }: { incomeType: IncomeType }) {
  return (
    <>
      <Paragraph fontType="bodySmallHeading">
        Income documents{' '}
        <TooltipPopper
          triggered={<Tooltip body={<Paragraph>{INCOME_DOC_ENCOURAGEMENT_TEXT[incomeType]}</Paragraph>} />}
        />
      </Paragraph>
    </>
  );
}

function UploadedIncomeDocs({ index }: { index: number }) {
  const { watch } = useFormContext();
  const documents: Array<Document> = watch(`currentIncomes[${index}].documents`) || [];
  const redactedDocInfo = useIncomeDocsRedaction();

  const docsWithRedactedInfo = documents.map((doc) => ({ ...doc, ...redactedDocInfo?.find((x) => x?.id === doc.id) }));
  return (
    <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" gap="xs">
      {docsWithRedactedInfo.map((document: Document, i) => (
        <IncomeDocPreview key={`${i}_${document.id}`} index={index} document={document} />
      ))}
    </Grid>
  );
}

function getIsRedactionComplete(document: Document) {
  if (!document.isRedactionCheckComplete) {
    return false;
  }

  if (document.isRedacted) {
    return true;
  }

  if (document.canBeRedacted) {
    return false;
  }

  return true;
}

function DeleteDocumentButton({
  documentId,
  incomeSourceIndex,
  onCompleted,
  ...buttonProps
}: {
  documentId: string;
  incomeSourceIndex: number;
  onCompleted?: () => void;
} & React.PropsWithChildren<Omit<LoadingButtonProps, 'onClick'>>) {
  const { setValue, getValues } = useFormContext();

  const [deleteDocumentsMutation, { loading }] = useMutation(DeleteDocumentsFromIncomeSourceDocument, {
    refetchQueries: [GetRentalApplicationIncomeDocsDocument],
    onCompleted: () => {
      const documents = getValues(`currentIncomes[${incomeSourceIndex}].documents`) as Array<Document>;
      setValue(
        `currentIncomes[${incomeSourceIndex}].documents`,
        documents.filter((d) => d.id !== documentId),
      );
      onCompleted?.();
    },
  });

  const onClick = async () => {
    if (!documentId) {
      return;
    }

    await deleteDocumentsMutation({
      variables: {
        incomeSourceId: getValues(`currentIncomes[${incomeSourceIndex}].id`),
        documentIds: [documentId],
      },
    });
  };

  return <LoadingButton {...buttonProps} onClick={onClick} loading={loading} />;
}

function IncomeDocPreview({ index, document }: { index: number; document: Document }) {
  const isDocumentReadyToView = document.editorUrl && getIsRedactionComplete(document);
  const isExpired = document.expirationDateTime && new Date(document.expirationDateTime).valueOf() < Date.now();
  return (
    <Grid gridColumnEnd={{ default: 'span 12', md_gte: 'span 6', lg_gte: 'span 4' }}>
      <Card cardType="secondary" cardElevation={0}>
        <CardSection>
          {isDocumentReadyToView ? (
            <a
              target="_blank"
              href={document.editorUrl as string}
              rel="noreferrer"
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <IncomeDocPreviewImage
                src={document.previewUrl as string}
                alt={`Preview image for ${document.fileName}`}
              />
            </a>
          ) : (
            <IncomeDocSpinnerSpacer display="flex" alignItems="center" justifyContent="center">
              {isExpired ? <Tag appearance="warning">Expired</Tag> : <Spinner />}
            </IncomeDocSpinnerSpacer>
          )}
        </CardSection>
        <CardSection>
          <Flex display="flex" justifyContent="space-between" alignItems="center">
            <Paragraph
              fontType="finePrintHeading"
              style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
            >
              {document?.fileName || `Income document`}
            </Paragraph>
            <Trigger
              triggered={({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }, close?: () => void) => (
                <ModalDialog
                  isOpen={isOpen}
                  onClose={onClose}
                  renderHeader={
                    <Heading level="6" as="h1">
                      Document deletion
                    </Heading>
                  }
                  renderBody={
                    <Paragraph>
                      Are you sure you want to delete{' '}
                      {document?.fileName ? <strong>{document?.fileName}</strong> : `this income document`}?
                    </Paragraph>
                  }
                  renderFooter={
                    <ButtonGroup aria-label="Delete income document actions">
                      <TextButton onClick={close} type="button">
                        Cancel
                      </TextButton>
                      <DeleteDocumentButton
                        documentId={document.id!}
                        incomeSourceIndex={index}
                        buttonType="caution"
                        type="button"
                        onCompleted={close}
                      >
                        Yes, delete
                      </DeleteDocumentButton>
                    </ButtonGroup>
                  }
                />
              )}
            >
              <IconButton
                disabled={document.id == null}
                icon={<IconTrashcanOutline />}
                type="button"
                bare
                size="xs"
                title="Delete document"
              />
            </Trigger>
          </Flex>
        </CardSection>
      </Card>
    </Grid>
  );
}

function IncomeDocsPrivacyProtection() {
  return (
    <Paragraph fontType="bodySmall" fontColor="textMedium">
      Sensitive information, like your SSN, may be automatically redacted when you upload a document. Preview your
      documents to verify that they do not include sensitive information.
    </Paragraph>
  );
}

function IncomeDocsUploader({ index }: { index: number }) {
  const { setValue, getValues, watch } = useFormContext();
  const { enqueueToast } = useToast();
  const documentsPath = `currentIncomes[${index}].documents`;
  const docsLength = (watch(documentsPath) as Array<DocumentInput>)?.length ?? 0;

  const MULTI_FILE_UPLOAD_ACCEPTED_FILES = ['.pdf', '.jpeg', '.jpg', '.png'];
  const PLACEHOLDER_TEXT = `Add up to ${MAX_DOCUMENTS_PER_INCOME} documents per income source. Password-protected and encrypted documents are not supported. Each file must be smaller than 100MB.`;
  const client = useApolloClient();
  const generateUploadToken = async () => {
    return (
      (await client.query({ query: GetRentalApplicationsDocumentUploadTokenDocument })).data
        .rentalApplicationsDocumentUploadToken ?? ''
    );
  };

  const [addDocumentMutation] = useMutation(AddDocumentsToIncomeSourceDocument, {
    refetchQueries: [GetRentalApplicationIncomeDocsDocument],
  });

  async function onUploadComplete(upload: ZDocsUpload) {
    // Temporarily add the "pending" document just to include it in the document length counter and in the preview,
    // otherwise we get a race condition where the document length is off by one during the "addDocumentMutation" duration
    setValue(documentsPath, [
      // We should use "getValues" instead of documents otherwise we'll capture the document array value at the time of the function call
      ...getValues(documentsPath),
      {
        messageToken: upload.messageToken,
        // We add the fileName here for the preview
        fileName: upload.file.name.replace(/\.[^.]+$/, ''),
      },
    ]);

    const { data: newDocs } = await addDocumentMutation({
      variables: {
        incomeSourceId: getValues(`currentIncomes[${index}].id`),
        documents: [{ messageToken: upload.messageToken }],
      },
    });

    setValue(
      documentsPath,
      // Since the mutation takes different time each time, this
      // gets executed out of order, so we substitute the item added above
      // to preserve the original order, we use flatMap since newDocs is an array,
      // even when we expect 1 document on the result array
      (getValues(documentsPath) as Array<DocumentInput>).flatMap((d) =>
        d.messageToken == upload.messageToken ? (newDocs?.addDocumentToIncomeSource ?? []) : [d],
      ),
    );
  }

  function onUploadFailed(failed: ZDocsUpload) {
    enqueueToast(<Toast appearance="error" body={failed.errorMessage} />);
  }

  const maxFiles = MAX_DOCUMENTS_PER_INCOME - docsLength;

  const [zDocsReset, setZDocsReset] = useState(1);
  return (
    maxFiles > 0 && (
      <ZDocsUploader
        key={zDocsReset}
        generateUploadToken={generateUploadToken}
        onUploadComplete={onUploadComplete}
        onUploadActive={(isUploadActive) => {
          if (!isUploadActive) {
            // Reset the ZDocs components when all uploads are complete
            setZDocsReset((x) => 1 - x);
          }
        }}
        onUploadFailed={onUploadFailed}
        placeholderText={PLACEHOLDER_TEXT}
        accept={MULTI_FILE_UPLOAD_ACCEPTED_FILES}
        autoUpload={true}
        maxFiles={maxFiles}
      />
    )
  );
}

function EmployerContact({ index }: { index: number }) {
  const {
    formState: { errors },
    register,
  } = useFormContext();

  const errorOrganization = get(errors, `currentIncomes[${index}].employer.organization`);
  const errorName = get(errors, `currentIncomes[${index}].employer.name`);
  const errorEmail = get(errors, `currentIncomes[${index}].employer.email`);
  const errorPhone = get(errors, `currentIncomes[${index}].employer.phone`);

  return (
    <>
      <FormField
        label={<Label>Employer</Label>}
        control={
          <Input
            maxLength={32}
            {...register(`currentIncomes[${index}].employer.organization`, { required: 'An employer is required' })}
          />
        }
        description={errorOrganization && <FormHelp>{errorOrganization.message}</FormHelp>}
        error={!!errorOrganization}
      />
      <Controller
        name={`currentIncomes[${index}].startDate`}
        rules={{ required: 'A started on date is required', validate: (v) => validateDate(v, { maxYearsAgo: 50 }) }}
        render={({ field, fieldState: { error, invalid } }) => (
          <FormField
            label={<Label>Started on</Label>}
            description={error && <FormHelp>{error?.message}</FormHelp>}
            error={invalid}
          >
            <AdornedInput
              input={<DatePickerInputMemoized {...field} minDate={date50YearsAgo()} />}
              rightAdornment={
                <Adornment>
                  <IconCalendarOutline size="sm" />
                </Adornment>
              }
            />
          </FormField>
        )}
      />
      <FormField
        label={<Label>Employer contact name</Label>}
        control={
          <Input
            maxLength={100}
            {...register(`currentIncomes[${index}].employer.name`, { required: 'A name is required' })}
          />
        }
        description={
          errorName ? (
            <FormHelp>{errorName.message}</FormHelp>
          ) : (
            <FormHelp>
              Please add the name of your manager or an HR representative who can verify your employment at this
              company.
            </FormHelp>
          )
        }
        error={!!errorName}
      />
      <FormField
        label={<Label>Employer contact email</Label>}
        control={
          <Input
            maxLength={100}
            {...register(`currentIncomes[${index}].employer.email`, { validate: validateEmail })}
            type="email"
          />
        }
        description={errorEmail && <FormHelp>{errorEmail.message}</FormHelp>}
        error={!!errorEmail}
        optional
      />
      <FormField
        label={<Label>Employer contact phone</Label>}
        control={
          <Controller
            name={`currentIncomes[${index}].employer.phone`}
            rules={{
              validate: (phone) => {
                if (!phone) return true;
                return isValidUSPhoneNumber(phone) || 'Enter a valid phone number';
              },
            }}
            render={({ field }) => <MaskedPhoneInput {...field} />}
          />
        }
        description={errorPhone && <FormHelp>{errorPhone.message}</FormHelp>}
        error={!!errorPhone}
        optional
      />
    </>
  );
}

export const CurrentIncomeRecommendation = {
  condition: ({ answers }: { answers: Answers }) => {
    const currentIncomes = answers.currentIncomes || [];
    return (
      currentIncomes.length > 0 && !currentIncomes.some((currentIncome) => (currentIncome?.documents || []).length > 0)
    );
  },
  body: (
    <Paragraph>
      More than 60% of applicants include income documents, which can help the rental manager make a faster decision
      about your application. Consider adding current income documents, such as pay stubs or W-2s, to your application.
    </Paragraph>
  ),
  title: 'Income documents',
};
